import React, { FC, useMemo } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { LayoutStrategy } from '@oolio-group/domain';
import { useTranslation } from '@oolio-group/localization';
import {
  getLayoutDescriptionKey,
  getLayoutNameKey,
} from '../../common/constants';
import { useColors } from '../../hooks/useColors';
import Icon from '../../component/Icon/Icon';
import LayoutSettingsStyles from './LayoutSettings.styles';

/**
 * Shape of the miniature schematic drawn at the top of each card: how many
 * columns of tiles it has, and how many tiles are stacked in each column.
 */
const PREVIEW_SHAPE: Record<LayoutStrategy, { columns: number; rows: number }> =
  {
    [LayoutStrategy.SINGLE_ROW]: { columns: 4, rows: 1 },
    [LayoutStrategy.DOUBLE_ROW]: { columns: 4, rows: 2 },
    [LayoutStrategy.FREE_FLOW]: { columns: 5, rows: 1 },
    [LayoutStrategy.GRID]: { columns: 3, rows: 2 },
  };

export interface LayoutCardProps {
  strategy: LayoutStrategy;
  selected: boolean;
  testID: string;
  onSelect: (strategy: LayoutStrategy) => void;
}

const LayoutCard: FC<LayoutCardProps> = ({
  strategy,
  selected,
  testID,
  onSelect,
}) => {
  const styles = LayoutSettingsStyles();
  const { colors } = useColors();
  const { translate } = useTranslation();

  const isFreeFlow = strategy === LayoutStrategy.FREE_FLOW;
  // Same fallback the key accessors apply: an unrecognised strategy gets the
  // Free Flow schematic rather than crashing on an undefined shape.
  const { columns, rows } =
    PREVIEW_SHAPE[strategy] ?? PREVIEW_SHAPE[LayoutStrategy.FREE_FLOW];

  // Free Flow tiles vary in height and sit top-aligned; every other strategy
  // fills its column evenly.
  const flowTileStyles = useMemo(
    () => [
      styles.flowTile0,
      styles.flowTile1,
      styles.flowTile2,
      styles.flowTile3,
      styles.flowTile4,
    ],
    [styles],
  );

  return (
    <TouchableOpacity
      testID={testID}
      accessibilityRole="radio"
      accessibilityState={{ checked: selected }}
      style={[styles.card, selected && styles.cardSelected]}
      onPress={() => onSelect(strategy)}
    >
      <View
        style={[styles.preview, isFreeFlow && styles.previewTopAligned]}
        testID={`${testID}-preview`}
      >
        {isFreeFlow
          ? Array.from({ length: columns }, (_, column) => (
              <View
                key={`tile-${column}`}
                style={[
                  styles.previewTile,
                  flowTileStyles[column % flowTileStyles.length],
                ]}
              />
            ))
          : Array.from({ length: columns }, (_, column) => (
              <View key={`column-${column}`} style={styles.previewColumn}>
                {Array.from({ length: rows }, (_, row) => (
                  <View key={`tile-${row}`} style={styles.previewTile} />
                ))}
              </View>
            ))}
      </View>
      <View style={styles.cardTitleRow}>
        <Text style={styles.cardTitle}>
          {translate(getLayoutNameKey(strategy))}
        </Text>
        <View style={[styles.indicator, selected && styles.indicatorSelected]}>
          {selected ? (
            <Icon name="check" size={12} color={colors.white} />
          ) : null}
        </View>
      </View>
      <Text style={styles.cardDescription}>
        {translate(getLayoutDescriptionKey(strategy))}
      </Text>
    </TouchableOpacity>
  );
};

export default LayoutCard;
